import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMap, faCheckCircle, faTimesCircle, faClock, faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '../../../_services/user.service';
import { ShippingConfigService } from '../../../_services/shipping-config.service';
import { CustomerService, CustomerSearchResult } from '../../../_services/customer.service';
import {
  ArticleDisposition,
  PackageSpeed,
  PickpointStation,
  PickpointStationShort,
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
  ShippingConfigType,
} from '../../../_models/shipping-config.interface';
import {
  PACKAGE_SPEEDS,
  SHIPMENT_METHODS,
  SHIPPING_TYPES,
  defaultDispositionForType,
  isPlaceholderTechnician,
  offsetWordingFor,
  shippingTypeDisabledReason,
  showTechnicianSelectorFor,
  visibleShipmentMethodsFor,
} from '../../../_models/shipping-config-rules';
import {
  STAGING_DELAY_WORKING_DAYS,
  ScheduleDefaultsInput,
  computeStagingShipReady,
  defaultPickupDate,
  defaultShippingOffsetDays,
  scheduleDefaultsFor,
  toDatetimeLocal,
} from '../../../_models/shipping-schedule-defaults';
import {
  pickupOpeningHint,
  pickupValidityError,
} from '../../../_models/shipping-pickup-validation';
import { PickpointMapPickerComponent } from './pickpoint-map-picker.component';

interface TechnicianOption { id: number; name: string; }
interface WarehouseOption { id: number; label: string; }

/**
 * Pure Formular-Komponente fuer das Erstellen/Bearbeiten einer Versandkonfiguration.
 *
 * Hat keine eigene HTTP-Persistenz: Context wird vom Parent als Input geliefert,
 * die Form emittiert beim Speichern die Datenform `ShippingConfigFormData`. Der
 * Parent ist fuer das HTTP-POST zustaendig (Single-Op-API, Bulk-API oder
 * beliebigen anderen Endpunkt).
 *
 * Techniker-Liste und Techniker-Lager werden intern ueber den `UserService`
 * geladen — diese Listen haengen vom `projectId`- bzw. `targetUserId`-Input ab
 * und sind in allen Einsatzkontexten gleich.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, FontAwesomeModule, PickpointMapPickerComponent],
  selector: 'hawk-shipping-config-form',
  templateUrl: './shipping-config-form.component.html',
})
export class ShippingConfigFormComponent implements OnInit, OnChanges {
  /** Bestehende Config fuer Edit-Modus; null oder undefined fuer Create-Modus. */
  @Input() initialConfig: ShippingConfig | null = null;
  /** Kontextinfos (Rueckadresse-Flag, Filialname) — vom Parent geladen. */
  @Input() context: ShippingConfigContext | null = null;
  /** Store-ID des Tickets — entscheidet ob `store_shipping` erlaubt ist. */
  @Input() ticketStoreId?: number;
  /** Fuer den PickPoint-Kartenpicker: Mittelpunkt. */
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  /** Stichtag fuer Station-Aktivitaet (optional). */
  @Input() referenceDate?: string;
  /** Project-ID zum Filtern der Technikerliste (optional). */
  @Input() projectId?: number;
  /** Vorausgewaehlter Techniker (z.B. Operations-Techniker bei Neu-Anlage). */
  @Input() operationTechnicianId?: number;
  /** Name des Auftragstechnikers — fuer die Anzeige im Regel-Modus ("vom Auftrag"). */
  @Input() operationTechnicianName?: string;
  /** Ueberschrift des Cards. `null` blendet sie aus. */
  @Input() heading: string | null = 'Versandkonfiguration';
  /** Primaer-Button-Label. */
  @Input() saveLabel = 'Speichern';
  /** Hinweistext unter dem Versanddatum. */
  @Input() shippingDateHint = 'Leer lassen für Versand am Auftragsdatum.';
  /**
   * true, wenn dieser Config bereits mind. ein Staging-Artikel zugeordnet ist.
   * Dann ist Kunden-Rueckversand gesperrt — Staging-Artikel koennen nicht ueber
   * einen Kunden-Rueckversand laufen (siehe canAssign/COVERING_TYPES in der
   * Versand-Matrix). Erst die Staging-Zuordnung loesen.
   */
  @Input() stagingArticlesAssigned = false;

  @Output() save = new EventEmitter<ShippingConfigFormData>();
  @Output() cancel = new EventEmitter<void>();

  // Icons
  iconMap = faMap;
  iconCheck = faCheckCircle;
  iconTimes = faTimesCircle;
  iconClock = faClock;
  iconInfo = faInfoCircle;
  iconWarn = faExclamationTriangle;

  // State
  showMapPicker = signal(false);
  selectedType = signal<ShippingConfigType>('pickpoint');
  selectedStation = signal<PickpointStationShort | null>(null);
  shipmentMethod = signal<ShipmentMethod | null>('parcel');
  packageSpeed = signal<PackageSpeed | null>('standard');
  articleDisposition = signal<ArticleDisposition>('technician_transfer');
  shippingDate = signal<string>('');
  // Pickup-Datum nur fuer PickPoint relevant. Format: `YYYY-MM-DDTHH:mm` fuer
  // <input type="datetime-local">. Beim Speichern wird ein leeres Feld als
  // null an den Server geschickt (Backend setzt dann den Default beim Create
  // bzw. behaelt den DB-Wert beim Update — siehe Controller).
  pickupDate = signal<string>('');
  // Frueheste-Versand-Zeitpunkt fuer Staging-Pakete (Verzoegerter Versand).
  // Leer = keine Verzoegerung. Format wie pickupDate ("YYYY-MM-DDTHH:mm").
  stagingShipReadyAt = signal<string>('');

  // Regel "Ziel-Techniker vom Auftrag": true = kein fester Techniker, Auflösung
  // dynamisch aus dem Auftragstechniker. Default AN bei Neu-Anlage der Typen mit
  // Techniker-Umbuchung (Regel als Standard).
  targetUserFromOperation = signal<boolean>(false);

  // Datums-Modus pro Kanal: 'fixed' = festes Datum, 'offset' = Regel "X Werktage
  // vor Auftragsdatum", 'window' = Versandfenster „Zeitraum vor Auftrag"
  // (frühestens earliestOffsetDays, spätestens shippingOffsetDays Werktage vor
  // Auftrag; nicht für PickPoint). Normaler Versand: Default 'offset' (Regel
  // als Standard). Staging hat zusätzlich 'standard' = keine Verzögerung
  // (Versand nach KW-Zuordnung durch die Staging-Config) und ist der Default.
  shippingDateMode = signal<'fixed' | 'offset' | 'window'>('fixed');
  // Initialwert nur als Vor-Render-Fallback; der echte Default kommt aus dem
  // Context (Gruppen-Vorlauf) bzw. dem gespeicherten Wert — siehe
  // applyInitialConfig / defaultShippingOffsetDays.
  shippingOffsetDays = signal<number>(12);
  // Frühester Versandtag des Fensters (Werktage vor Auftrag, nur im
  // window-Modus relevant). Muss >= shippingOffsetDays sein.
  earliestOffsetDays = signal<number>(10);
  stagingShipMode = signal<'fixed' | 'offset' | 'standard'>('standard');
  stagingOffsetDays = signal<number>(5);

  technicians = signal<TechnicianOption[]>([]);
  technicianWarehouses = signal<WarehouseOption[]>([]);
  loadingWarehouses = signal(false);

  // Signals fuer Felder, die in `canSave` einfliessen — sonst triggert
  // ngModel-Tippen kein Recompute (Button bleibt disabled).
  targetUserId = signal<number | null>(null);
  pmCode = signal('');

  // Form-Felder ohne Einfluss auf `canSave` (direkt mit [(ngModel)] gebunden)
  targetWarehouseId: number | null = null;
  // Technikerversand-Adresse: legt das Buero fest, der Lagerist kann in der
  // LagerApp dann nicht mehr waehlen. Default 'warehouse' (Lageradresse).
  technicianShipTarget = signal<'private' | 'warehouse'>('warehouse');

  // Erweiterter Bereich (collapsible). Enthaelt Ziel-Techniker, Ziel-Lager,
  // Notizen und ggf. den PM-Code (sofern er erfolgreich vom System-User
  // vorbelegt werden konnte). Default zugeklappt — beim Bearbeiten einer
  // bestehenden Config wird automatisch ausgeklappt, wenn dort Werte stehen.
  showAdvancedSection = signal<boolean>(false);

  // True, wenn der PM-Code aus dem System-User (ID 1) erfolgreich vorbelegt
  // wurde — dann landet der PM-Code im erweiterten Bereich, weil er nur
  // selten manuell ueberschrieben werden muss. Andernfalls bleibt er sichtbar
  // (und Pflicht), damit der User ihn manuell eintragen kann.
  pmCodeAutoLoaded = signal<boolean>(false);

  canOpenMap = computed(() =>
    this.ticketLat != null && this.ticketLng != null &&
    !Number.isNaN(this.ticketLat) && !Number.isNaN(this.ticketLng),
  );

  showTechnicianSelector = computed(() =>
    showTechnicianSelectorFor(this.selectedType(), this.articleDisposition()),
  );

  // Auftragstechniker ist ein Platzhalter-/System-User (Bentomax Lager 709 /
  // nicht vergeben 283) → im Regel-Modus ("vom Auftrag") loest sich kein echter
  // Ziel-Techniker auf (Backend: primary_technician = nil). Die LagerApp warnt
  // dann und blockiert den Versand, bis am Auftrag ein echter Techniker steht.
  // Getter (kein computed), weil die Inputs ausserhalb des Signal-Graphen liegen.
  get operationTechnicianIsPlaceholder(): boolean {
    return isPlaceholderTechnician(this.operationTechnicianId);
  }

  // Versandfenster-Plausibilität: der früheste Versandtag (mehr Werktage vor
  // Auftrag) muss vor oder auf dem spätesten liegen — sonst wäre das Fenster
  // leer. Backend validiert authoritativ; hier nur für Hinweis + canSave-Gate.
  readonly windowRangeInvalid = computed(() => this.shippingDateMode() === 'window'
    && Number(this.earliestOffsetDays()) < Number(this.shippingOffsetDays()));

  // Wechsel in den Fenster-Modus: konsistente Vorbelegung sicherstellen. Beim
  // Umschalten aus dem Offset-Modus (z.B. 12 WT Vorlauf) wäre das Fenster mit
  // dem stehengebliebenen earliest-Wert sonst sofort invalide — dann auf die
  // Fenster-Defaults aus dem Context (8–10) zurückfallen, notfalls auf ein
  // Punkt-Fenster (frühestens = spätestens).
  protected selectWindowMode(): void {
    this.shippingDateMode.set('window');
    if (Number(this.earliestOffsetDays()) >= Number(this.shippingOffsetDays())) return;

    const win = this.context?.technician_window_defaults;
    if (win) {
      this.shippingOffsetDays.set(win.latest);
      this.earliestOffsetDays.set(win.earliest);
    } else {
      this.earliestOffsetDays.set(Number(this.shippingOffsetDays()));
    }
  }

  canSave = computed(() => {
    if (this.shipmentMethod() == null) return false;
    if (this.shipmentMethod() === 'parcel' && this.packageSpeed() == null) return false;
    if (this.windowRangeInvalid()) return false;

    // PM-Code ist Pflicht bei PickPoint.
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) return false;

    switch (this.selectedType()) {
      case 'pickpoint':
        // Im Regel-Modus (Techniker vom Auftrag) ist kein fester Techniker noetig.
        if (this.selectedStation() == null || (this.targetUserId() == null && !this.targetUserFromOperation())) return false;
        // Abholzeit muss innerhalb Aktivierungszeitraum + Wochentags-Oeffnungs-
        // zeiten der gewaehlten Station liegen — selbe Logik wie im
        // Sonderversand-Popup.
        return this.pickupValidityError() == null;
      case 'store_shipping':
        if (this.ticketStoreId == null) return false;
        return this.articleDisposition() === 'consumed' || this.targetUserId() != null || this.targetUserFromOperation();
      case 'technician_shipping':
        return this.targetUserId() != null || this.targetUserFromOperation();
      case 'customer_shipping':
        // Mit zugeordneten Staging-Artikeln nicht erlaubt (Staging laeuft nie
        // ueber Kunden-Rueckversand).
        if (this.stagingArticlesAssigned) return false;
        // Ziel-Kunde gewaehlt → der muss eine Ruecksendeadresse haben.
        if (this.targetCustomerId() != null) return this.selectedCustomerHasReturn();
        // Internes Projekt ohne Ziel-Kunden → Auswahl ist Pflicht.
        if (this.context?.project_is_internal) return false;
        // Sonst Fallback: Projekt-Kunde muss eine Ruecksendeadresse haben.
        return this.context?.customer_has_return_address === true;
      default: return false;
    }
  });

  // Abholzeit-Validierung gegen Aktivierungszeitraum + Wochentags-Oeffnungs-
  // zeiten der gewählten PickPoint-Station (Logik in
  // _models/shipping-pickup-validation.ts). Wird im Template als Warnung
  // angezeigt und gated `canSave`.
  readonly pickupValidityError = computed<string | null>(() =>
    this.selectedType() !== 'pickpoint'
      ? null
      : pickupValidityError(this.selectedStation(), this.pickupDate()),
  );

  // Anzeigetext fuer die Oeffnungszeiten am gewaehlten Tag (oder null).
  readonly pickupOpeningHint = computed<string | null>(() =>
    this.selectedType() !== 'pickpoint'
      ? null
      : pickupOpeningHint(this.selectedStation(), this.pickupDate()),
  );

  // Staging-Versand-Verzoegerung ist fuer alle Empfaengertypen mit Staging-
  // Bedarf relevant — nur beim reinen Kunden-Rueckversand (consumed) gibt es
  // keine Staging-Pakete.
  showStagingDelay = computed(() => this.selectedType() !== 'customer_shipping');

  // Wording der Offset-Regel je Versandtyp: PickPoint rechnet ab dem Abholtermin
  // des Abholauftrags (pickup_date), alle anderen Typen ab dem Auftragsdatum —
  // spiegelt ShippingConfig#rule_reference_date im Backend.
  readonly offsetWording = computed(() => offsetWordingFor(this.selectedType()));

  visibleShippingTypes = computed(() => this.shippingTypes);

  shippingTypes = SHIPPING_TYPES;

  shipmentMethods = SHIPMENT_METHODS;

  visibleShipmentMethods = computed(() => visibleShipmentMethodsFor(this.selectedType()));

  packageSpeeds = PACKAGE_SPEEDS;

  constructor(
    private userService: UserService,
    private shippingConfigService: ShippingConfigService,
    private customerService: CustomerService,
  ) {}

  // === Ziel-Kunde fuer Kunden-Rueckversand (v.a. interne Projekte) ===
  targetCustomerId = signal<number | null>(null);
  selectedCustomerName = signal<string>('');
  // Hat der gewaehlte Ziel-Kunde eine Ruecksendeadresse? Steuert canSave.
  selectedCustomerHasReturn = signal<boolean>(false);
  customerSearch = '';
  customerResults = signal<CustomerSearchResult[]>([]);
  showCustomerDropdown = signal<boolean>(false);

  onCustomerSearch(term: string): void {
    const q = term.trim();
    if (q.length < 2) {
      this.customerResults.set([]);
      this.showCustomerDropdown.set(false);
      return;
    }
    this.customerService.searchCustomers(q).subscribe({
      next: (results) => {
        this.customerResults.set(results);
        this.showCustomerDropdown.set(true);
      },
      error: (err) => console.error('Kundensuche fehlgeschlagen', err),
    });
  }

  selectCustomer(customer: CustomerSearchResult): void {
    this.targetCustomerId.set(customer.id);
    this.selectedCustomerName.set(customer.name);
    this.selectedCustomerHasReturn.set(customer.has_return_address);
    this.customerSearch = '';
    this.customerResults.set([]);
    this.showCustomerDropdown.set(false);
  }

  clearTargetCustomer(): void {
    this.targetCustomerId.set(null);
    this.selectedCustomerName.set('');
    this.selectedCustomerHasReturn.set(false);
  }

  ngOnInit(): void {
    this.applyInitialConfig();
    this.loadTechnicians();
    const uid = this.targetUserId();
    if (uid != null) {
      this.loadWarehouses(uid);
    }
    // PM-Code aus System-User (ID 1) vorbelegen, sofern PickPoint aktiv ist
    // und der Code noch leer ist (neue Config oder bestehende Config ohne
    // gesetzten PM-Code).
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) {
      this.loadMasterPmCode();
    }
    // Erweiterte Einstellungen sind grundsaetzlich eingeklappt — der User
    // klickt aktiv darauf, wenn er sie braucht.
  }

  private async loadMasterPmCode(): Promise<void> {
    const code = await this.shippingConfigService.getMasterPmCode();
    if (code) {
      this.pmCode.set(code);
      this.pmCodeAutoLoaded.set(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialConfig'] && !changes['initialConfig'].firstChange) {
      this.applyInitialConfig();
      const uid = this.targetUserId();
      if (uid != null) {
        this.loadWarehouses(uid);
      }
    }
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      // Techniker-Liste neu laden (Projekt-Filter)
      this.technicians.set([]);
      this.loadTechnicians();
    }
    // Context (Auftragsgruppe + Default-Offset) trifft oft erst nach dem ersten
    // Render ein — dann die Regel-Vorlaufzeit nachziehen, solange der User den
    // Offset nicht selbst gesetzt/getippt hat:
    //  - Create + Offset-Modus (Default-Anlage), oder
    //  - Edit OHNE gespeicherten Offset, solange noch im Fest-Datum-Modus
    //    (das Offset-Feld ist dann verborgen → gefahrlos den latenten Default
    //    aktualisieren, damit ein spaeteres Umschalten den richtigen Wert zeigt).
    if (changes['context']) {
      const noStoredOffset = this.initialConfig?.shipping_date_offset_days == null;
      const createOffset = !this.initialConfig && this.shippingDateMode() === 'offset';
      const editLatent = !!this.initialConfig && noStoredOffset && this.shippingDateMode() === 'fixed';
      if (createOffset || editLatent) {
        this.shippingOffsetDays.set(defaultShippingOffsetDays(this.selectedType(), this.context));
      }
      // Interner Materialversand: festen Ziel-Techniker aus dem Filialnamen
      // vorbelegen (Context trifft i.d.R. erst nach dem ersten Render ein).
      this.applyInternalStoreTechnician();
    }
  }

  isTypeDisabled(type: ShippingConfigType): boolean {
    return this.typeDisabledReason(type) !== null;
  }

  typeDisabledReason(type: ShippingConfigType): string | null {
    return shippingTypeDisabledReason(type, {
      ticketStoreId: this.ticketStoreId,
      stagingArticlesAssigned: this.stagingArticlesAssigned,
    });
  }

  // Einmalig angewandt: interner-Materialversand-Default (fester Techniker aus
  // dem Ticketfiliale-Namen). Verhindert, dass eine spaetere Context-Aenderung
  // eine bereits getroffene Userwahl ueberschreibt.
  private internalTechApplied = false;

  // Interner Materialversand: ist im Context ein ueber den Filialnamen
  // aufgeloester Techniker hinterlegt, beim NEU-Anlegen einen FESTEN Ziel-
  // Techniker vorbelegen (statt der Auftrags-Regel) — aber nur, solange der User
  // noch nichts Eigenes gewaehlt hat (Regel-Modus ohne festen Techniker).
  private applyInternalStoreTechnician(): void {
    if (this.initialConfig || this.internalTechApplied) return;
    const id = this.context?.internal_store_technician_id;
    if (id == null) return;
    if (!this.targetUserFromOperation() || this.targetUserId() != null) return;

    this.internalTechApplied = true;
    this.ensureMatchedTechnicianOption();
    this.targetUserFromOperation.set(false);
    this.onTechnicianChange(id);
  }

  // Stellt sicher, dass der ueber den Filialnamen aufgeloeste Techniker im
  // Dropdown auswaehlbar ist (er ist evtl. kein Projekt-Techniker und fehlt
  // dann in der normalen Liste).
  private ensureMatchedTechnicianOption(): void {
    const id = this.context?.internal_store_technician_id;
    const name = this.context?.internal_store_technician_name;
    if (id == null || !name) return;
    if (this.technicians().some(t => t.id === id)) return;
    this.technicians.set(
      [...this.technicians(), { id, name }].sort((a, b) => a.name.localeCompare(b.name)),
    );
  }

  // Umschalten Techniker-Regel (vom Auftrag) ↔ fester Techniker. Beim Wechsel in
  // den Regel-Modus den festen Techniker/Lager verwerfen.
  toggleTargetUserFromOperation(on: boolean): void {
    this.targetUserFromOperation.set(on);
    if (on) {
      this.targetUserId.set(null);
      this.targetWarehouseId = null;
      this.technicianWarehouses.set([]);
    }
  }

  onTechnicianChange(userId: number | null): void {
    this.targetUserId.set(userId);
    this.targetWarehouseId = null;
    if (userId != null) {
      this.loadWarehouses(userId);
    } else {
      this.technicianWarehouses.set([]);
    }
  }

  setShipmentMethod(method: ShipmentMethod): void {
    this.shipmentMethod.set(method);
    if (method === 'parcel') {
      if (this.packageSpeed() == null) this.packageSpeed.set('standard');
    } else {
      this.packageSpeed.set(null);
    }
  }

  // Wechselt den Versandtyp und setzt unzulaessige Versandarten zurueck —
  // PickPoint erlaubt nur Paket und Kurier; bei Wechsel von z.B. pallet
  // auf pickpoint waere shipmentMethod sonst inkonsistent.
  // Beim Wechsel zu PickPoint wird ausserdem der Master-PM-Code geladen,
  // sofern das Feld leer ist (z.B. weil vorher eine andere Config-Variante
  // ohne PM-Code aktiv war).
  setShippingType(type: ShippingConfigType): void {
    if (this.isTypeDisabled(type)) return;
    const previousType = this.selectedType();
    this.selectedType.set(type);
    if (type === 'pickpoint') {
      const m = this.shipmentMethod();
      if (m !== 'parcel' && m !== 'courier') {
        this.setShipmentMethod('parcel');
      }
      if (!this.pmCode().trim()) {
        this.loadMasterPmCode();
      }
      // Wechsel auf PickPoint: Default-Pickup-Vorschlag einblenden, falls
      // das Feld leer ist (z.B. weil vorher Filial- oder Technikerversand
      // aktiv war). Muss VOR applyShippingDefaultsForType passieren.
      if (!this.pickupDate()) {
        this.pickupDate.set(defaultPickupDate(this.referenceDate));
      }
    }
    // Bei echtem Typ-Wechsel Versand-/Staging-Datum nach dem Typ-Schema neu
    // setzen: PickPoint → beide 5 Tage vor Abholzeitpunkt; sonst Versanddatum
    // 14 Tage vor Auftragsdatum, Staging-Verzoegerung leer. Re-Klick auf den
    // bereits aktiven Typ aendert nichts (keine versehentliche Ueberschreibung).
    if (previousType !== type) {
      this.applyShippingDefaultsForType(type);
    }
  }

  onMapStationPicked(station: PickpointStation | null): void {
    if (!station) return;
    this.selectedStation.set({
      id: station.id,
      pp_id: station.pp_id,
      name: station.name,
      address_zip: station.address_zip,
      address_city: station.address_city,
      active: station.active,
      // Fuer die Abholzeit-Validierung weiterreichen — beim Edit-Flow kommen
      // dieselben Felder aus dem ShippingConfig-Hash (siehe Backend).
      openings: station.openings,
      activation_date: station.activation_date,
      cancellation_date: station.cancellation_date,
    });
  }

  emitSave(): void {
    const type = this.selectedType();
    const disposition = defaultDispositionForType(type, this.articleDisposition());

    // Datums-Regel: im Offset-Modus die "X Werktage vorher"-Regel schicken (und
    // das feste Datum verwerfen), sonst das feste Datum. Felder immer mitgeben
    // (auch null), damit ein Wechsel zurueck auf festes Datum die Regel loescht.
    // Fenster-Modus = Offset-Regel (spätester Tag) + earliest-Offset (frühester
    // Tag); für PickPoint nicht wählbar (Guard doppelt zum Backend-Sanitize).
    const useShippingWindow = this.shippingDateMode() === 'window' && type !== 'pickpoint';
    const useShippingOffset = this.shippingDateMode() === 'offset' || useShippingWindow;
    const stagingRelevant = type !== 'customer_shipping';
    const useStagingOffset = stagingRelevant && this.stagingShipMode() === 'offset';
    // 'standard' = keine Verzoegerung → weder fester Termin noch Offset.
    const useStagingFixed = stagingRelevant && this.stagingShipMode() === 'fixed';

    const data: ShippingConfigFormData = {
      shipping_type: type,
      shipment_method: this.shipmentMethod()!,
      package_speed: this.shipmentMethod() === 'parcel' ? this.packageSpeed() : null,
      article_disposition: disposition,
      shipping_date: useShippingOffset ? null : (this.shippingDate() || null),
      shipping_date_offset_days: useShippingOffset ? Number(this.shippingOffsetDays()) : null,
      earliest_shipping_offset_days: useShippingWindow ? Number(this.earliestOffsetDays()) : null,
      staging_ship_ready_at: useStagingFixed ? (this.stagingShipReadyAt() || null) : null,
      staging_ship_offset_days: useStagingOffset ? Number(this.stagingOffsetDays()) : null,
      pm_code: type === 'pickpoint' ? this.pmCode().trim() : null,
      pickup_date: type === 'pickpoint' ? this.pickupDate() || null : null,
    };

    // Techniker-Regel: im Regel-Modus keinen festen Techniker/Lager mitschicken.
    const ruleTech = this.showTechnicianSelector() && this.targetUserFromOperation();

    switch (type) {
      case 'pickpoint':
        data.pickpoint_station_id = this.selectedStation()!.id;
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTech ? null : this.targetUserId();
        data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        break;
      case 'store_shipping':
        if (disposition === 'consumed') {
          data.target_user_id = null;
          data.target_warehouse_id = null;
        } else {
          data.target_user_from_operation = this.targetUserFromOperation();
          data.target_user_id = ruleTech ? null : this.targetUserId();
          data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        }
        break;
      case 'technician_shipping':
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTech ? null : this.targetUserId();
        data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        data.technician_ship_target = this.technicianShipTarget();
        break;
      case 'customer_shipping':
        // Optionaler Ziel-Kunde (Pflicht bei internen Projekten) — leer =
        // Fallback auf den Projekt-Kunden im Backend.
        data.target_customer_id = this.targetCustomerId();
        break;
    }

    this.save.emit(data);
  }

  private applyInitialConfig(): void {
    const c = this.initialConfig;
    if (c) {
      this.selectedType.set(c.shipping_type);
      this.selectedStation.set(c.pickpoint_station ?? null);
      // Beim Bearbeiten exakt den DB-Stand uebernehmen — KEIN Fallback auf den
      // Auftragstechniker, sonst sieht es so aus, als waere er gespeichert.
      // Auto-Configs (z.B. fuer interne Auftraege) duerfen ja explizit ohne
      // festen Techniker existieren.
      this.targetUserId.set(c.target_user_id ?? null);
      this.targetWarehouseId = c.target_warehouse_id ?? null;
      // Regel "Techniker vom Auftrag" aus dem DB-Stand uebernehmen.
      this.targetUserFromOperation.set(c.target_user_from_operation ?? false);
      // Datums-Modus aus dem DB-Stand: gesetztes Fenster (earliest-Offset) →
      // window-Modus (MUSS vor dem Offset-Check stehen, sonst frisst dieser den
      // Fenster-Fall), gesetzter Offset → Regel-Modus, sonst festes Datum.
      if (c.earliest_shipping_offset_days != null) {
        this.shippingDateMode.set('window');
        this.shippingOffsetDays.set(c.shipping_date_offset_days ?? defaultShippingOffsetDays(this.selectedType(), this.context));
        this.earliestOffsetDays.set(c.earliest_shipping_offset_days);
      } else if (c.shipping_date_offset_days != null) {
        this.shippingDateMode.set('offset');
        this.shippingOffsetDays.set(c.shipping_date_offset_days);
      } else {
        this.shippingDateMode.set('fixed');
        // Kein gespeicherter Offset: das (noch verborgene) Offset-Feld mit dem
        // Gruppen-Default aus dem Context vorbelegen, damit ein Umschalten auf
        // "X Werktage vor Auftrag" den korrekten Default zeigt — nicht den
        // Signal-Initialwert.
        this.shippingOffsetDays.set(defaultShippingOffsetDays(this.selectedType(), this.context));
      }
      // Staging-Modus aus dem DB-Stand (3 Modi): gesetzter Offset → 'offset';
      // gesetzter fester Termin (oder aktive PickPoint-Config ohne Termin → -5-WT-
      // Default, s. ready_at-Block unten) → 'fixed'; sonst keine Verzoegerung →
      // 'standard' (Versand nach KW-Zuordnung).
      if (c.staging_ship_offset_days != null) {
        this.stagingShipMode.set('offset');
        this.stagingOffsetDays.set(c.staging_ship_offset_days);
      } else if (c.staging_ship_ready_at) {
        this.stagingShipMode.set('fixed');
      } else if (c.shipping_type === 'pickpoint' && !c.shipped_at) {
        // Aktive PickPoint-Config ohne gespeicherte Staging-Verzoegerung:
        // Offset-Default 5 WT (einheitlich mit der Neuanlage).
        this.stagingShipMode.set('offset');
        this.stagingOffsetDays.set(STAGING_DELAY_WORKING_DAYS);
      } else {
        this.stagingShipMode.set('standard');
      }
      // Techniker-Versandadresse aus dem DB-Stand; ohne gesetzten Wert Default
      // 'warehouse' (Lageradresse).
      this.technicianShipTarget.set(c.technician_ship_target ?? 'warehouse');
      // Ziel-Kunde fuer Kunden-Rueckversand aus dem DB-Stand. Wurde beim Speichern
      // gegen seine Ruecksendeadresse validiert → als vorhanden behandeln.
      this.targetCustomerId.set(c.target_customer_id ?? null);
      this.selectedCustomerName.set(c.target_customer_name ?? '');
      this.selectedCustomerHasReturn.set(c.target_customer_id != null);
      this.shipmentMethod.set(c.shipment_method ?? 'parcel');
      this.packageSpeed.set(c.shipment_method === 'parcel' ? (c.package_speed ?? 'standard') : null);
      this.articleDisposition.set(c.article_disposition ?? defaultDispositionForType(c.shipping_type));
      this.shippingDate.set(c.shipping_date ?? '');
      this.pmCode.set(c.pm_code ?? '');
      // Backend liefert ISO-8601 mit Sekunden/Zone — datetime-local braucht
      // "YYYY-MM-DDTHH:mm". Bei bestehender Config nehmen wir den DB-Wert;
      // bei einer PickPoint-Config ohne pickup_date (z.B. Type-Wechsel von
      // store_shipping → pickpoint) fallen wir auf den Default-Vorschlag
      // (operation_date - 1h) zurueck, damit das Feld nicht leer bleibt.
      const pickupFromDb = c.pickup_date ? toDatetimeLocal(c.pickup_date) : '';
      this.pickupDate.set(pickupFromDb || (c.shipping_type === 'pickpoint' ? defaultPickupDate(this.referenceDate) : ''));
      // Verzoegerung: gesetzten DB-Wert exakt uebernehmen. Ist sie bei einer
      // AKTIVEN PickPoint-Config noch leer, den −5-Tage-Vorschlag vorbefuellen
      // (PickPoint soll standardmaessig verzoegert versandt werden — gilt auch
      // beim Bearbeiten frisch auto-generierter Configs, z.B. im Sonderversand).
      if (c.staging_ship_ready_at) {
        this.stagingShipReadyAt.set(toDatetimeLocal(c.staging_ship_ready_at));
      } else if (c.shipping_type === 'pickpoint' && !c.shipped_at) {
        this.stagingShipReadyAt.set(computeStagingShipReady(this.scheduleInput()));
      } else {
        this.stagingShipReadyAt.set('');
      }
    } else {
      this.selectedType.set('pickpoint');
      this.selectedStation.set(null);
      // Auftragstechniker vorbelegen — aber Platzhalter-User (Lager / nicht
      // vergeben) nicht uebernehmen (analog Backend-Auto-Create).
      const opTech = this.operationTechnicianId ?? null;
      this.targetUserId.set(isPlaceholderTechnician(opTech) ? null : opTech);
      this.targetWarehouseId = null;
      // Regel als Standard: neuer Auftrag → Ziel-Techniker dynamisch vom Auftrag.
      this.targetUserFromOperation.set(true);
      this.technicianShipTarget.set('warehouse');
      this.clearTargetCustomer();
      this.shipmentMethod.set('parcel');
      this.packageSpeed.set('standard');
      this.articleDisposition.set('technician_transfer');
      this.pmCode.set('');
      this.pickupDate.set(defaultPickupDate(this.referenceDate));
      // Versand- + Staging-Datum nach Typ-Schema vorbefuellen (Default-Typ
      // PickPoint → beide 5 Tage vor Abholzeitpunkt). pickupDate muss vorher
      // gesetzt sein, weil der PickPoint-Vorschlag darauf aufbaut.
      this.applyShippingDefaultsForType(this.selectedType());
    }
  }

  // Bindet die Input-Werte an die zentralen Termin-Defaults; die Logik selbst
  // liegt in _models/shipping-schedule-defaults.ts (gemeinsam genutzt mit dem
  // geführten Versandziel-Dialog auf der Ticketseite).
  private scheduleInput(): ScheduleDefaultsInput {
    return {
      context: this.context,
      referenceDate: this.referenceDate,
      pickupDate: this.pickupDate(),
    };
  }

  // Setzt Versanddatum + Staging-Verzögerung nach dem Typ-Schema (Neu-Anlage
  // UND Typ-Wechsel). null-Werte aus den Defaults heissen "nicht anfassen" —
  // der latente Wert im Formular bleibt dann stehen.
  private applyShippingDefaultsForType(type: ShippingConfigType): void {
    const defaults = scheduleDefaultsFor(type, this.scheduleInput());
    this.shippingDateMode.set(defaults.shippingDateMode);
    this.shippingOffsetDays.set(defaults.shippingOffsetDays);
    if (defaults.earliestOffsetDays != null) this.earliestOffsetDays.set(defaults.earliestOffsetDays);
    this.shippingDate.set(defaults.shippingDate);
    this.stagingShipMode.set(defaults.stagingShipMode);
    if (defaults.stagingOffsetDays != null) this.stagingOffsetDays.set(defaults.stagingOffsetDays);
    this.stagingShipReadyAt.set(defaults.stagingShipReadyAt);
  }

  private loadTechnicians(): void {
    if (this.technicians().length > 0) return;
    this.userService.getTechnicians(this.projectId ? { project_id: this.projectId } : {}).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || [])
          // Platzhalter-/System-User (Bentomax Lager, nicht vergeben) nicht als
          // Ziel-Techniker waehlbar — gespiegelt zur Backend-Validierung.
          .filter(u => !isPlaceholderTechnician(u.id))
          .map(u => ({
            id: u.id,
            name: u.full_name || u.name || `#${u.id}`,
          } as TechnicianOption));
        mapped.sort((a, b) => a.name.localeCompare(b.name));
        this.technicians.set(mapped);
        // Den ggf. ueber den Filialnamen aufgeloesten Techniker (interner
        // Materialversand) sicher in die Liste aufnehmen — er ist evtl. kein
        // Projekt-Techniker und fehlt sonst im Dropdown.
        this.ensureMatchedTechnicianOption();
      },
      error: err => console.error('Techniker konnten nicht geladen werden', err),
    });
  }

  private loadWarehouses(userId: number): void {
    this.loadingWarehouses.set(true);
    this.userService.getUsersWarehouses(userId).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || []).map(w => ({ id: w.id, label: w.label } as WarehouseOption));
        this.technicianWarehouses.set(mapped);

        const current = this.targetWarehouseId;
        if (current != null && !mapped.some(w => w.id === current)) {
          this.targetWarehouseId = mapped[0]?.id ?? null;
        } else if (current == null && mapped.length === 1) {
          this.targetWarehouseId = mapped[0].id;
        }
        this.loadingWarehouses.set(false);
      },
      error: err => {
        console.error('Lager konnten nicht geladen werden', err);
        this.technicianWarehouses.set([]);
        this.loadingWarehouses.set(false);
      },
    });
  }
}
